export type VariantId = "a" | "b";
export type FlowVariant = VariantId;

// Answer key used to track whether the second story card on the story_pair
// journey step has been revealed by the user's first "Next" click.
export const STORY_PAIR_REVEAL_KEY = "__storyPairRevealed";

export type FunnelId = "faith-reconnect" | "family-devotional";

export interface FunnelTheme {
  id: string;
  className: string;
}

export interface FunnelDefinition {
  id: FunnelId;
  slug: string;
  name: string;
  aliases: readonly string[];
  defaultVariant: VariantId;
  theme: FunnelTheme;
  getSteps: (variant: VariantId) => FunnelStep[];
}

export type StepKind =
  | "welcome"
  | "question"
  | "affirmation"
  | "loading"
  | "result"
  | "journey_feature"
  | "email"
  | "payment"
  | "story_recommendation";

export type QuestionSelection = "single" | "multi";

export interface FunnelOption {
  id: string;
  label: string;
  description?: string;
  emoji?: string;
  image?: string;
  badge?: string;
}

interface BaseStep {
  id: string;
  order: number;
  kind: StepKind;
  title: string;
  subtitle?: string;
  progress?: number;
  confirmLabel?: string;
  icon?: string;
}

export interface WelcomeHeroSlide {
  image: string;
  chipLabel: string;
  eyebrow: string;
  subject: string;
}

export interface WelcomeFeaturePill {
  icon: string;
  label: string;
}

export interface WelcomeStep extends BaseStep {
  kind: "welcome";
  description: string[];
  ratingText: string;
  confirmLabel: string;
  /** Ordered slides shown in the top welcome carousel; each slide carries its own control chip label. */
  heroSlides?: WelcomeHeroSlide[];
  featurePills?: WelcomeFeaturePill[];
  /** Static hero image shown below the trust badge; also the video's poster. */
  heroImage?: string;
  /** When set, the hero renders as a looping video with heroImage as its poster. */
  heroVideo?: { webm: string; mp4: string };
  finePrint?: string;
  /** Optional text shown below the hero image (e.g. "1,000+ easy to read pieces of wisdom"). */
  topicsText?: string;
}

export interface FunnelOptionFeedback {
  text: string;
  variant?: "positive" | "encouraging";
}

export interface QuestionStep extends BaseStep {
  kind: "question";
  selection: QuestionSelection;
  options: FunnelOption[];
  helperText?: string;
  mediaLabel?: string;
  /** When set, shown above the title (e.g. illustration for the question). */
  mediaImage?: string;
  minSelections?: number;
  maxSelections?: number;
  answerKey?: string;
  /** When set, shows a feedback message after selection instead of auto-advancing. */
  optionFeedback?: Record<string, FunnelOptionFeedback>;
}

export interface AffirmationStep extends BaseStep {
  kind: "affirmation";
  description: string[];
  confirmLabel: string;
  /** Optional image shown above the title. */
  heroImage?: string;
  /** Enables rounded corners for `heroImage` when true. */
  heroImageHasBorderRadius?: boolean;
  /** Pixel value for `heroImage` border radius (defaults to 20). */
  heroImageBorderRadius?: number;
  /** Pixel value for `heroImage` bottom margin. Uses CSS default when omitted. */
  heroImageMarginBottom?: number;
  mediaLabel?: string;
  mediaPlacement?: "above-title" | "after-description";
}

export interface LoadingStep extends BaseStep {
  kind: "loading";
  loadingFact: string;
  modalQuestion: {
    answerKey: string;
    title: string;
    options: FunnelOption[];
  };
  confirmLabel: string;
  /** Optional image shown above the title instead of the step icon. */
  heroImage?: string;
  heroImageHasBorderRadius?: boolean;
  heroImageBorderRadius?: number;
  heroImageMarginBottom?: number;
}

export interface ResultStep extends BaseStep {
  kind: "result";
  /** When false, renders the configured persona copy instead of deriving one from answers. */
  useComputedPersona?: boolean;
  heroImage?: string;
  /** Pixel value for `heroImage` bottom margin. Uses CSS default when omitted. */
  heroImageMarginBottom?: number;
  /** Optional CSS mask image path for `heroImage` (white/lighter areas stay visible). */
  heroImageMask?: string;
  personaName: string;
  description: string[];
  questMap: Array<{
    id: string;
    label: string;
    percent: number;
    icon: string;
  }>;
  confirmLabel: string;
}

export type JourneyFeatureLayout =
  | "story_pair"
  | "chat_feature"
  | "daily_verse";

export interface JourneyFeatureStep extends BaseStep {
  kind: "journey_feature";
  layout: JourneyFeatureLayout;
  description?: string[];
  heroImage?: string;
  confirmLabel: string;
}

export interface EmailStep extends BaseStep {
  kind: "email";
  description: string[];
  emailPlaceholder: string;
  confirmLabel: string;
  secondaryCta?: string;
  trustNote: string;
  quotes: Array<{
    text: string;
    author: string;
    weeksUsingApp?: number;
    image?: string;
  }>;
  answerKey: string;
}

export interface PaymentPlanOption {
  id: string;
  label: string;
  savePercent: number;
  weeksInPeriod: number;
  totalPrice: string;
  isMostPopular?: boolean;
  currencyCode?: string;
  unitAmount?: number;
  minorUnitDivisor?: number;
  offer?: { planId: string; promoCode: string; firstPeriodAmount: number; discountDuration: "once" | "forever" };
}

export type TestimonialConcern = "consistency" | "understanding" | "family" | "ease";

export interface PaymentStep extends BaseStep {
  kind: "payment";
  description: string[];
  benefits: string[];
  plans: PaymentPlanOption[];
  proofBullets: string[];
  testimonials: Array<{
    concern?: TestimonialConcern;
    text: string;
    author: string;
    weeksUsingApp: number;
    image?: string;
  }>;
  noChargeNote: string;
  securityNotes: string[];
  confirmLabel: string;
  whyCardCopy: string;
  answerKey: string;
}

export interface StoryRecommendationStep extends BaseStep {
  kind: "story_recommendation";
  description: string[];
  confirmLabel: string;
}

export type FunnelStep =
  | WelcomeStep
  | QuestionStep
  | AffirmationStep
  | LoadingStep
  | ResultStep
  | JourneyFeatureStep
  | EmailStep
  | PaymentStep
  | StoryRecommendationStep;

export type AnswerValue = string | string[];

export type AnswersByKey = Record<string, AnswerValue | undefined>;
